import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import adMobService from '../services/AdMobService';
import apiService from '../services/ApiService';
import authService from '../services/AuthService';
import gameStateManager from '../services/GameStateManager';
import configService from '../services/ConfigService';

async function preloadResultsData() {
  const user = authService.getUser();
  try {
    const topicData = await apiService.get('/api/v1/topics/today');

    const [playersCount, schedulerTime, yourWords] = await Promise.all([
      apiService.get('/api/v1/game/players-today').then(d => d.count).catch(() => null),
      configService.getSchedulerTimeAsync(),
      user && topicData
        ? apiService.get(`/api/v1/game/user-words/${topicData.id}`).catch(async () =>
            (await gameStateManager.getWords(user.id, topicData.id)).map((raw) => ({ raw, canonical: null }))
          )
        : Promise.resolve([]),
    ]);

    let finalResults = null;
    let provisionalResults = null;
    let isAfterScheduler = false;
    let awaitingProcessing = false;

    if (topicData) {
      const data = await apiService
        .get(`/api/v1/game/final-results/${topicData.id}`)
        .catch(() => null);
      const results = data?.results ?? (Array.isArray(data) ? data : []);
      if (Array.isArray(results) && results.length > 0) {
        finalResults = results;
        isAfterScheduler = true;
      } else if (configService.isPastSchedulerTime()) {
        isAfterScheduler = true;
        awaitingProcessing = true;
      } else {
        provisionalResults = await apiService
          .get('/api/v1/game/provisional-results')
          .catch(() => []);
      }
    }

    const userStreak = user
      ? await apiService.get('/api/v1/game/user-streak').then(d => d?.streak || 0).catch(() => 0)
      : 0;

    const previousData = await (async () => {
      try {
        const prevTopic = await apiService.get('/api/v1/topics/previous');
        if (prevTopic?.hasResults) {
          const [prevResultsData, prevWords] = await Promise.all([
            apiService.get(`/api/v1/game/final-results/${prevTopic.id}`),
            user
              ? apiService.get(`/api/v1/game/user-words/${prevTopic.id}`)
              : Promise.resolve([]),
          ]);
          const prevResults = prevResultsData?.results ?? (Array.isArray(prevResultsData) ? prevResultsData : []);
          return { prevTopic, prevResults, prevWords: prevWords || [] };
        }
      } catch {}
      return null;
    })();

    return {
      topic: topicData,
      yourWords: yourWords || [],
      playersCount,
      schedulerTime,
      finalResults,
      provisionalResults,
      isAfterScheduler,
      awaitingProcessing,
      userStreak,
      previousData,
    };
  } catch (err) {
    console.error('[AdPage] preload error:', err);
    return null;
  }
}

export default function AdPage() {
  const navigate = useNavigate();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      // Precargar datos de resultados en paralelo con el anuncio
      const preloadPromise = preloadResultsData();

      // El anuncio ya se precarga al iniciar la app; si por algún motivo no
      // está listo todavía, se prepara aquí como último recurso.
      if (Capacitor.isNativePlatform()) {
        const ready = adMobService.interstitialReady || await adMobService.prepareInterstitial();
        if (ready) await adMobService.showInterstitial();
      }

      // Esperar a los datos (normalmente ya estarán listos)
      const preloaded = await preloadPromise;
      navigate('/results', { replace: true, state: { preloaded } });
    };

    run();
  }, [navigate]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: 2,
    }}>
      <CircularProgress sx={{ color: 'white' }} />
      <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
        Cargando resultados...
      </Typography>
    </Box>
  );
}
